import moment from "moment";
import { formatAppointmentDateTime, formattedDate } from "../utils/dateUtils";
import api from "./Api";
import { preventDuplicateRequest } from "../utils/requestManager";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}
export interface AssignedUser {
  userId: number;
  username: string;
  fullname: string;
  emailId: string;
  departmentName: string | null;
  genderName: string | null;
  phoneNumber: string | null;
  userTypeName: string;
  userRoleName: string;
  teamName: string | null;
}

const historian = process.env.REACT_APP_CASE_URL || "";
if (!historian) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const HistorianAPI = api(historian);

const getHistorianCardSearch = async (historianData: any): Promise<any> => {
  const key = `getHistorianCardSearch-${JSON.stringify(historianData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await HistorianAPI.post<ApiResponse<any>>(
        "/api/historian/summary",
        historianData,
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const getHistorianSimpleSearch = async (historianData: any): Promise<any> => {
  const key = `getHistorianSimpleSearch-${JSON.stringify(historianData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await HistorianAPI.post<ApiResponse<any>>(
        "/api/historian/schedule",
        historianData,
      );
      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        scheduledDate: formatAppointmentDateTime(data.scheduledDate),
        appointmentDatetime: formatAppointmentDateTime(
          data.appointmentDatetime,
        ),
        dateAssigned: formatAppointmentDateTime(data.dateAssigned),
        dueDate: formattedDate(data.dueDate),
      }));

      return updatedResult;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const UpdateScheduleDateByHistorian = async (params: any): Promise<any> => {
  try {
    const response = await HistorianAPI.put(
      "/api/historian/details/update",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const getHistorianDetailsByQMEID = async (
  subjectQMEScheduleId: any,
): Promise<any> => {
  try {
    const response = await HistorianAPI.get<ApiResponse<any>>(
      `/api/historian/status/details?subjectQMEScheduleId=${subjectQMEScheduleId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      createdDate: moment(data.createdDate).format("MM/DD/YYYY - hh:mm A"),
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};
const getHistorianRecordsEmail = async (
  subjectQMEScheduleId: number,
): Promise<any> => {
  try {
    const response = await HistorianAPI.get<ApiResponse<any>>(
      `/api/historian/email/history-uploaded?subjectQmeScheduleId=${subjectQMEScheduleId}`,
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getHistorianExceptionEmail = async (
  subjectQMEScheduleId: any,
): Promise<any> => {
  try {
    const response = await HistorianAPI.get<ApiResponse<any>>(
      `/api/historian/email/exception?subjectQmeScheduleId=${subjectQMEScheduleId}`,
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};
const GetAssignedToHistorianList = async (
  loggedInUserId: Number
): Promise<ApiResponse<AssignedUser[]> | undefined> => {
  try {
    const response = await HistorianAPI.get(
      `/api/historian/assignee/list?`
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};
export {
  getHistorianCardSearch,
  getHistorianSimpleSearch,
  UpdateScheduleDateByHistorian,
  getHistorianDetailsByQMEID,
  getHistorianRecordsEmail,
  getHistorianExceptionEmail,
  GetAssignedToHistorianList,
};
